
import { computed, defineComponent, reactive } from 'vue'
import { useRouter } from 'vue-router'

import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

import StepCompany from '@/modules/CreateOrderModule/components/StepCompany/index.vue'
import StepDateFtlCreate from '@/modules/CreateOrderModule/components/StepDateFtlCreate/index.vue'
import StepCarFtlCreate from '@/modules/CreateOrderModule/components/StepCarFtlCreate/index.vue'
import StepCargoFtlCreate from '@/modules/CreateOrderModule/components/StepCargoFtlCreate/index.vue'
import StepWaypoints from '@/modules/CreateOrderModule/components/NewStepWaypoints/index.vue'
import StepCreate from '@/modules/CreateOrderModule/components/StepCreate/index.vue'
import FtlExcelMultipleModal from '@/modules/CreateOrderModule/components/FtlExcelMultipleModal/index.vue'

import {
    CargoFTLBidT,
    CompletedAdditionalServicesT,
    CompletedCargoFTLBidT,
} from '@/core/types/create_order.types'

import { CarInfoFTLBidT, NewWaypointT, NewFTLBidCreatePayloadT } from '@/core/types/ftl_bids.types'

import { ftl_bids } from '@/core/realisations/ftl_bids.realisation'

import useNotifications from '@/common/composable/useNotifications'
import { useMaska } from '@/common/composable/useMaska'

import { useFormData } from '@/common/composable/useFormData'

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        StepCompany,
        StepDateFtlCreate,
        StepCarFtlCreate,
        StepCargoFtlCreate,
        StepWaypoints,
        StepCreate,
        FtlExcelMultipleModal,
    },
    setup() {
        const notifications = useNotifications()
        const router = useRouter()
        const { unmaskNumbers } = useMaska()
        const { getFormData } = useFormData()

        const state = reactive({
            company_id: null as number | null,
            //
            take_date: null as string | null,
            take_time: null as string | null,
            ftl_type: 2,
            //
            additional_service: null as CompletedAdditionalServicesT | null,
            //
            car_info: null as CarInfoFTLBidT | null,
            //
            completed_cargos: [] as CompletedCargoFTLBidT[],
            completed_cargo: null as CargoFTLBidT | null,
            //
            is_updating_cargo: false,
            updating_cargo_index: null as number | null,
            updating_cargo: null as CompletedCargoFTLBidT | null,
            waypoints: [] as NewWaypointT[],
            //
            is_creating_ftl_bid: false,
            is_ftl_created: false,
            is_step_cargo_finished: false,
            is_step_car_finished: false,
            is_on_step_waypoints: false,
            is_on_step_car: false,
        })

        const excel_multiple_state = reactive({
            is_opened: false,
        })

        // Companies actions
        const onCompanySubmit = (company_id: number) => {
            state.company_id = company_id
        }

        const onCompanyReset = () => {
            state.company_id = null
            state.ftl_type = 2
        }

        const isCompanySelected = computed(() => Boolean(state.company_id))

        // Date actions
        const onDateSubmit = ({
            ftl_type,
            take_date,
            take_time,
        }: {
            ftl_type: number
            take_date: string
            take_time: string
        }) => {
            state.ftl_type = ftl_type
            state.take_date = take_date
            state.take_time = take_time
        }

        const onDateReset = () => {
            state.ftl_type = 2
            state.take_date = null
            state.take_time = null
        }

        const isDateSelected = computed(() => Boolean(state.take_date && state.take_time))

        // Step Car ftl
        const onCarSubmit = (car: CarInfoFTLBidT) => {
            state.car_info = car

            state.is_on_step_waypoints = true
            state.is_on_step_car = false
            state.is_step_car_finished = true
        }

        const onCarReset = () => {
            state.car_info = null

            state.is_on_step_waypoints = false
            state.is_on_step_car = true
            state.is_step_car_finished = false
        }

        const isCarSelected = computed(() => Boolean(state.car_info))

        // Step cargo ftl bid
        const cancelUpdateCargo = () => {
            state.is_updating_cargo = false
            state.is_on_step_car = true
            state.updating_cargo = null
            state.is_step_cargo_finished = true
        }

        const onCargoSubmit = (cargo: CargoFTLBidT) => {
            if (state.is_updating_cargo) {
                const completed_cargo: CompletedCargoFTLBidT = {
                    logistics_info: cargo,
                }

                state.completed_cargo = completed_cargo.logistics_info

                cancelUpdateCargo()
                return
            }

            const completed_cargo: CompletedCargoFTLBidT = {
                logistics_info: cargo,
            }
            state.completed_cargo = completed_cargo.logistics_info

            state.is_on_step_car = true
            state.is_step_cargo_finished = true
        }

        const onStartUpdateCargo = () => {
            if (!state.completed_cargo) return
            state.is_on_step_car = false

            state.updating_cargo = { logistics_info: { ...state.completed_cargo } }
            state.is_updating_cargo = true
            state.is_step_cargo_finished = false
        }

        // Waypoints
        const onSubmitWaypoints = (waypoints: NewWaypointT[]) => {
            state.waypoints = waypoints
            state.is_on_step_waypoints = !state.is_on_step_waypoints
        }

        const onUpdateWaypoints = () => {
            state.waypoints = []
            state.is_on_step_waypoints = false
        }

        const isOnCreate = computed(() => {
            return state.completed_cargo && isDateSelected.value
        })

        const createPayload = () => {
            const payload: NewFTLBidCreatePayloadT = {
                dop_order_number: state.completed_cargo?.dop_order_number as string,
                special_conditions: state.completed_cargo?.special_conditions,
                transport_type: state.car_info?.transport_type,
                payment_method: state.completed_cargo?.payment_method,
                is_need_car: state.car_info?.is_need_car,
                car_info: {
                    cubature: state.car_info?.cubature,
                    load_capacity: state.car_info?.load_capacity,
                    loading_type: state.car_info?.loading_type,
                    oversized_cargo: state.car_info?.oversized_cargo,
                    temperature_mode: state.car_info?.temperature_mode,
                },
                is_stacked: state.car_info?.is_stacked,
                is_dangerous: state.car_info ? state.car_info.is_dangerous : 0,
                invoice_files: state.car_info ? state.car_info.invoice_files : [],
                certificate_of_safety_files: state.car_info
                    ? state.car_info.certificate_of_safety_files
                    : [],
                shipment_type: state.completed_cargo?.shipment_type,
                waypoints: state.waypoints,
                //
                client_price: state.completed_cargo?.client_price
                    ? Number(unmaskNumbers(state.completed_cargo?.client_price))
                    : 0,
                insurance: state.completed_cargo?.insurance,
                is_ensure: state.car_info ? state.car_info.is_ensure : 0,
            }

            if (state.company_id) payload.company_id = state.company_id
            if (state.take_date) payload.take_date = state.take_date
            if (state.take_time) payload.take_time = state.take_time
            if (state.car_info?.description) payload.description = state.car_info?.description
            if (state.car_info?.is_ensure) {
                payload.cargo_name = state.car_info?.cargo_name
                payload.declared_price = state.car_info?.declared_price
                    ? Number(unmaskNumbers(state.car_info?.declared_price))
                    : 0
            }

            payload.weight = state.completed_cargo?.weight || 0
            payload.height = state.completed_cargo?.height || 0
            payload.width = state.completed_cargo?.width || 0
            payload.depth = state.completed_cargo?.depth || 0
            payload.places = state.completed_cargo?.places || 0

            payload.ftl_type = state.ftl_type

            return payload
        }

        const onSubmitOrder = async () => {
            try {
                state.is_creating_ftl_bid = true

                await ftl_bids.createFtl(getFormData(createPayload()))

                notifications.notify({
                    title: 'FTL-заказ создан',
                    type: 'success',
                })

                state.is_ftl_created = true
                await router.push('/ftl')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При создании FTL-заказа произошла ошбика',
                })
            } finally {
                state.is_creating_ftl_bid = false
            }
        }

        const resetOrder = () => {
            state.is_ftl_created = false

            state.updating_cargo = null
            state.is_updating_cargo = false

            state.take_date = null
            state.take_time = null

            state.ftl_type = 2

            state.company_id = null

            state.is_step_cargo_finished = false
            state.waypoints = []
        }

        const openExcelMultipleModal = () => {
            excel_multiple_state.is_opened = true
        }

        const closeExcelMultipleModal = () => {
            excel_multiple_state.is_opened = false
        }

        const onExcelMultipleOrderCreated = () => {
            closeExcelMultipleModal()
            router.push('/')
        }

        return {
            state,
            excel_multiple_state,
            //
            onCompanySubmit,
            onCompanyReset,
            //
            onDateSubmit,
            onDateReset,
            //
            onCarSubmit,
            onCarReset,
            //
            cancelUpdateCargo,
            onCargoSubmit,
            onStartUpdateCargo,
            //
            onSubmitWaypoints,
            onUpdateWaypoints,
            //
            isCompanySelected,
            isDateSelected,
            isCarSelected,
            isOnCreate,
            //
            onSubmitOrder,
            resetOrder,
            openExcelMultipleModal,
            closeExcelMultipleModal,
            onExcelMultipleOrderCreated
        }
    },
})
